import {
  HAS_EXTEND_PROCESS_KEY,
  LOGINTIME_KEY,
  RND_KEY,
  SESSION,
  UIN,
} from '@repo/common-utils/constants/user'
import { SKILLHUB_LEAD_FLAG, SKILLHUB_LEAD_VALUE } from '@repo/common-utils/constants/skillhubLeadAuth'
import {
  hasSkillhubLeadAuth,
  hasUcSession,
} from '@repo/common-utils/utils/ucSessionAndLeadStorage'

/** 与 UC / 引流态相关的 localStorage 键，开户 H5 同域时会覆盖 */
const AUTH_KEYS = [
  SESSION,
  UIN,
  RND_KEY,
  LOGINTIME_KEY,
  HAS_EXTEND_PROCESS_KEY,
  'session3',
  SKILLHUB_LEAD_FLAG,
  SKILLHUB_LEAD_VALUE,
] as const

const BACKUP_PREFIX = 'skillhubAuthBackup:'
const PENDING_EXT_H5_KEY = 'skillhubPendingExtH5'
/** 从 Skill 平台跳转同域 H5（如开户）后，允许恢复登录态的最长时间 */
const PENDING_EXT_H5_MAX_MS = 30 * 60 * 1000

function backupKey(key: string): string {
  return `${BACKUP_PREFIX}${key}`
}

/** 打开同域 H5 前，将当前登录态快照写入 sessionStorage（仅本 Tab 可见） */
export function backupSkillhubAuthSnapshot(): void {
  if (!hasUcSession() && !hasSkillhubLeadAuth()) return
  try {
    for (const key of AUTH_KEYS) {
      const value = localStorage.getItem(key)
      if (value != null) {
        sessionStorage.setItem(backupKey(key), value)
      } else {
        sessionStorage.removeItem(backupKey(key))
      }
    }
  } catch {
    /* ignore */
  }
}

function restoreSkillhubAuthSnapshot(): boolean {
  try {
    const hasBackup = AUTH_KEYS.some((key) => sessionStorage.getItem(backupKey(key)) != null)
    if (!hasBackup) return false

    for (const key of AUTH_KEYS) {
      const value = sessionStorage.getItem(backupKey(key))
      if (value != null) {
        localStorage.setItem(key, value)
      } else {
        localStorage.removeItem(key)
      }
    }
    return hasUcSession() || hasSkillhubLeadAuth()
  } catch {
    return false
  }
}

export function clearSkillhubAuthSnapshot(): void {
  try {
    for (const key of AUTH_KEYS) {
      sessionStorage.removeItem(backupKey(key))
    }
    sessionStorage.removeItem(PENDING_EXT_H5_KEY)
  } catch {
    /* ignore */
  }
}

/** 标记即将打开同域 H5（开户等），并备份登录态 */
export function markPendingExternalH5Navigation(): void {
  backupSkillhubAuthSnapshot()
  try {
    sessionStorage.setItem(PENDING_EXT_H5_KEY, String(Date.now()))
  } catch {
    /* ignore */
  }
}

/**
 * 同域 H5 覆盖 localStorage 后，若仍在 Skill 平台且为外链跳转场景，则恢复登录态。
 */
export function tryRestoreAfterExternalH5Navigation(): boolean {
  try {
    const pendingAt = sessionStorage.getItem(PENDING_EXT_H5_KEY)
    if (!pendingAt) return false

    const age = Date.now() - Number(pendingAt)
    if (!Number.isFinite(age) || age < 0 || age > PENDING_EXT_H5_MAX_MS) {
      clearSkillhubAuthSnapshot()
      return false
    }

    if (hasUcSession() || hasSkillhubLeadAuth()) {
      clearSkillhubAuthSnapshot()
      return false
    }

    const restored = restoreSkillhubAuthSnapshot()
    if (restored) {
      clearSkillhubAuthSnapshot()
    }
    return restored
  } catch {
    return false
  }
}
